/**
 * Broadcast Connection Manager
 *
 * Handles peer connection to the broadcast host.
 * Manages connection lifecycle, timeouts, and connection state.
 *
 * This is feature-specific to Quickstart and knows about:
 * - Playcast peer connection
 * - Broadcast ID from matchmaking
 * - Connection timeouts
 */

import {
  generateAccessPolicyMessage,
  AccessPolicyMessageType,
  type WebsocketMessage,
} from '@playcastdotio/Messaging';
import { connectPeer } from '../../../api/modules/player';
import { sendMessage } from '../../../api/modules/realtimeMessaging';
import { registerShareCodeCallbacks } from '../../../api/modules/realtimeMessaging/handlers/accessPolicyMessage';
import { playerConnectionState } from '../../../api/stores/playerStores';
import { setPeerMilestoneCallbacks, isGuestReconnectInProgress } from '../../../api/modules/peer';
import type { QuickstartState } from '../QuickstartController';
import { GUEST_RECONNECT_UI_TIMEOUT_MS } from '@playcastdotio/PeerCore';

export interface BroadcastConnectionManagerConfig {
  /**
   * Get current state
   */
  getState: () => QuickstartState;

  /**
   * Update state
   */
  updateState: (state: Partial<QuickstartState>) => void;

  /**
   * Transition to a new phase
   */
  transitionTo: (phase: string) => void;

  /**
   * Called when connection succeeds
   */
  onConnected: () => void;

  /**
   * Called when connection fails
   */
  onConnectionFailed: (reason: string) => void;

  /**
   * Called when peer disconnects during play (after successful connection)
   */
  onDisconnected?: (kickReason: string) => void;

  /**
   * Called when PeerCore is auto-retrying a transient WebRTC failure.
   */
  onReconnecting?: (kickReason: string) => void;

  /**
   * Send analytics events
   */
  sendBroadcastJoinAttempted: () => void;
  sendBroadcastJoinFailed: (
    reason: string,
    duration: number,
    errorStage?: 'signaling' | 'sdp' | 'ice' | 'dtls' | 'auth' | 'timeout' | 'unknown'
  ) => void;
  sendBroadcastJoinSucceeded: (duration: number) => void;
  sendPeerIdAcquired?: (peerId: string) => void;
  sendSdpOfferSent?: () => void;
  sendSdpAnswerReceived?: () => void;
  sendIceCandidatesSent?: (candidateCount: number) => void;
  sendIceCheckingStarted?: () => void;
  sendIceConnected?: () => void;
  sendRemoteStreamReceived?: () => void;

  /**
   * Mid-play recovery completed (status re-promoted to 'connected' while we were already
   * joined). No join analytics — but the coordinator must clear its reconnecting state so
   * the reconnect cover lifts.
   */
  onReconnectRecovered?: () => void;

  /**
   * Log slow sequences to Sentry
   */
  logSlowSequence?: (sequenceName: string, duration: number, context?: Record<string, unknown>) => void;

  /** Called when a share-code resolve error occurs */
  onShareCodeError?: (errorCode: string, errorMessage: string) => void;
}

/**
 * Manager for broadcast peer connection
 */
export class BroadcastConnectionManager {
  private config: BroadcastConnectionManagerConfig;
  private connectionTimeout?: NodeJS.Timeout;
  private connectionTimeoutMs = GUEST_RECONNECT_UI_TIMEOUT_MS;
  private connectionListenerId?: string;
  private signedContract: unknown = null;
  private pendingShareCode?: string;
  private unregisterShareCodeCallbacks?: () => void;
  // Self-owned join classification. Reading shared coordinator state for this is unsafe:
  // MatchmakingManager's listener writes playerConnectionId on the same 'connected' dispatch
  // and always runs first (subscribed before connectToBroadcast), which would misclassify
  // every initial join as a mid-play recovery.
  private connectedThisAttempt = false;

  constructor(config: BroadcastConnectionManagerConfig) {
    this.config = config;
    this.unregisterShareCodeCallbacks = registerShareCodeCallbacks(
      (broadcastId, contract) => this.onShareCodeResolved(broadcastId, contract),
      (errorCode, errorMessage) => this.onShareCodeError(errorCode, errorMessage),
    );
  }

  public getSignedContract(): unknown {
    return this.signedContract;
  }

  /**
   * Resolve a share code to a broadcast connection.
   * Sends ACCESS_POLICY_RESOLVE, waits for the response, then connects.
   */
  public resolveShareCode(shareCode: string): void {
    const msg = generateAccessPolicyMessage(
      AccessPolicyMessageType.ACCESS_POLICY_RESOLVE,
      { shareCode },
    );
    sendMessage(msg as unknown as WebsocketMessage);
    this.pendingShareCode = shareCode;
  }

  /**
   * Called by the access policy message handler when resolve succeeds.
   */
  public onShareCodeResolved(broadcastId: string, contract: unknown): void {
    this.signedContract = contract;
    this.config.updateState({ broadcastId });
    this.connectToBroadcast();
  }

  /**
   * Called by the access policy message handler when resolve fails.
   */
  public onShareCodeError(errorCode: string, errorMessage: string): void {
    if (this.config.onShareCodeError) {
      this.config.onShareCodeError(errorCode, errorMessage);
    } else {
      this.config.onConnectionFailed(`Share code error: ${errorCode} - ${errorMessage}`);
    }
  }

  /**
   * Start connecting to broadcast
   */
  public connectToBroadcast(connectionTimeoutMs = GUEST_RECONNECT_UI_TIMEOUT_MS): void {
    const state = this.config.getState();
    const broadcastId = state.broadcastId;

    if (!broadcastId) {
      console.error('[BroadcastConnectionManager] No broadcast ID');
      this.config.onConnectionFailed('No broadcast ID');
      return;
    }

    console.log('[BroadcastConnectionManager] Connecting to broadcast:', broadcastId);

    this.connectedThisAttempt = false;
    this.config.updateState({
      connectionStartTime: Date.now(),
      // A fresh join is never a continuation: clear any id from a previous session so
      // analytics readers don't see a stale identity for this attempt.
      playerConnectionId: undefined,
    });

    this.config.transitionTo('joining_broadcast');
    this.config.sendBroadcastJoinAttempted();

    this.armConnectionTimeout(connectionTimeoutMs);

    // Remove any stale listener from a previous connectToBroadcast call (e.g. reconnect)
    // to prevent duplicate callbacks from stacked listeners
    if (this.connectionListenerId !== undefined) {
      playerConnectionState.removeListener(this.connectionListenerId);
      this.connectionListenerId = undefined;
    }

    // Subscribe to player connection state
    this.connectionListenerId = playerConnectionState.addListener((newState) => {
      console.log('[BroadcastConnectionManager] Connection state:', newState.status);

      const currentState = this.config.getState();
      const wasConnected = this.connectedThisAttempt;

      if (newState.status === 'connected') {
        // Mid-play ICE recovery re-promotes status without a new join — skip analytics
        // and onBroadcastConnected (which would demote playing_active → playing_afk).
        if (wasConnected) {
          // The reclaim may have rotated the peer id during recovery; keep the stored id
          // in sync so analytics/exit payloads report the live identity.
          if (newState.playerPeerId && newState.playerPeerId !== currentState.playerConnectionId) {
            this.config.updateState({ playerConnectionId: newState.playerPeerId });
          }
          // Tell the coordinator the recovery completed — without this the reconnect cover
          // stays latched over the live stream.
          this.config.onReconnectRecovered?.();
          return;
        }
        this.handleConnectionSuccess(newState.playerPeerId);
      } else if (newState.status === 'reconnecting' && wasConnected) {
        console.log('[BroadcastConnectionManager] Peer reconnecting during play:', newState.kickReason);
        this.config.onReconnecting?.(newState.kickReason || 'unknown');
      } else if (newState.status === 'failed' || newState.status === 'refused') {
        // Initial connection failure (before we were ever connected)
        this.handleConnectionFailure(newState.kickReason || 'Connection failed');
      } else if (newState.status === 'disconnected' && wasConnected) {
        // Mid-play disconnect (we were connected, now we're not)
        console.log('[BroadcastConnectionManager] Peer disconnected during play:', newState.kickReason);
        this.config.onDisconnected?.(newState.kickReason || 'unknown');
      } else if (newState.status === 'disconnected' && !wasConnected) {
        // Initial connection failure (disconnected before we connected)
        this.handleConnectionFailure(newState.kickReason || 'Connection failed');
      }
    });

    // Register WebRTC milestone callbacks for analytics
    setPeerMilestoneCallbacks({
      onPeerIdAcquired: (peerId) => this.config.sendPeerIdAcquired?.(peerId),
      onSdpOfferSent: () => this.config.sendSdpOfferSent?.(),
      onSdpAnswerReceived: () => this.config.sendSdpAnswerReceived?.(),
      onIceCandidatesSent: (count) => this.config.sendIceCandidatesSent?.(count),
      onIceChecking: () => this.config.sendIceCheckingStarted?.(),
      onIceConnected: () => this.config.sendIceConnected?.(),
      onRemoteStreamReceived: () => this.config.sendRemoteStreamReceived?.(),
    });

    // Initiate connection
    connectPeer(broadcastId);
  }

  private isPeerCoreStillReconnecting(): boolean {
    return isGuestReconnectInProgress() || playerConnectionState.currentState.status === 'reconnecting';
  }

  private armConnectionTimeout(connectionTimeoutMs: number): void {
    this.connectionTimeoutMs = connectionTimeoutMs;
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
    }
    this.connectionTimeout = setTimeout(() => {
      if (this.isPeerCoreStillReconnecting()) {
        this.armConnectionTimeout(this.connectionTimeoutMs);
        return;
      }
      console.error(`[BroadcastConnectionManager] Connection timeout (${connectionTimeoutMs}ms)`);
      this.config.onConnectionFailed('Connection timeout');
    }, connectionTimeoutMs);
  }

  /**
   * Handle successful connection
   */
  private handleConnectionSuccess(playerPeerId?: string): void {
    this.connectedThisAttempt = true;
    // Clear timeout
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = undefined;
    }

    // Keep the listener active to detect disconnects during play
    // (Don't remove it like we do on failure)

    console.log('[BroadcastConnectionManager] Connection succeeded:', playerPeerId);

    // Calculate connection duration
    const state = this.config.getState();
    const duration = state.connectionStartTime ? Date.now() - state.connectionStartTime : 0;

    this.config.updateState({
      playerConnectionId: playerPeerId,
    });

    // Log to Sentry if > 3 seconds
    if (this.config.logSlowSequence) {
      const totalDuration = state.pageLoadTime ? Date.now() - state.pageLoadTime : undefined;
      this.config.logSlowSequence('broadcast_connection', duration, {
        totalDuration,
        broadcastId: state.broadcastId,
        playerPeerId,
      });
    }

    this.config.sendBroadcastJoinSucceeded(duration);
    this.config.onConnected();
  }

  /**
   * Handle connection failure
   */
  private handleConnectionFailure(reason: string): void {
    if (this.isPeerCoreStillReconnecting()) {
      return;
    }
    // Clear timeout
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = undefined;
    }

    // Unsubscribe from connection state
    if (this.connectionListenerId !== undefined) {
      playerConnectionState.removeListener(this.connectionListenerId);
      this.connectionListenerId = undefined;
    }

    console.error('[BroadcastConnectionManager] Connection failed:', reason);

    // Calculate connection duration
    const state = this.config.getState();
    const duration = state.connectionStartTime ? Date.now() - state.connectionStartTime : 0;

    // Determine error stage from reason
    let errorStage: 'signaling' | 'sdp' | 'ice' | 'dtls' | 'auth' | 'timeout' | 'unknown' = 'unknown';
    const lowerReason = reason.toLowerCase();
    if (lowerReason.includes('timeout')) {
      errorStage = 'timeout';
    } else if (lowerReason.includes('ice')) {
      errorStage = 'ice';
    } else if (lowerReason.includes('sdp')) {
      errorStage = 'sdp';
    } else if (lowerReason.includes('signal')) {
      errorStage = 'signaling';
    } else if (lowerReason.includes('auth')) {
      errorStage = 'auth';
    } else if (lowerReason.includes('dtls')) {
      errorStage = 'dtls';
    }

    this.config.sendBroadcastJoinFailed(reason, duration, errorStage);
    this.config.onConnectionFailed(reason);
  }

  /**
   * Cleanup connection resources
   */
  public cleanup(): void {
    this.unregisterShareCodeCallbacks?.();
    this.unregisterShareCodeCallbacks = undefined;
    setPeerMilestoneCallbacks({});

    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = undefined;
    }

    if (this.connectionListenerId !== undefined) {
      playerConnectionState.removeListener(this.connectionListenerId);
      this.connectionListenerId = undefined;
    }
  }
}

